import * as React from 'react';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Loader2, AlertCircle, Download, Check, Minus } from 'lucide-react';
import { auth } from '../../firebase';

interface ScreenerRunEntry {
  id: string;
  runId: string;
  category: 'large' | 'mid' | 'small';
  timestamp: any;
  weekGroup: number;
  activeSectors: string[];
  stage1: { rawCandidates: number; finalCandidates: number };
  stage2: {
    rejections: {
      insufficientData: number;
      netIncomeNeg: number;
      fcfNeg: number;
      fcfSwings: number;
      interestCoverage: number;
      roicFloor: number;
      oeNotRising: number;
    };
    survivors: number;
    qualifiedSurvivors: number;
    watchSurvivors: number;
  };
  stage3: { resultsCount: number; nearMissesCount: number };
}

interface FunnelLogEntry {
  id: string;
  symbol: string;
  timestamp: any;
  category: string;
  qualityScore: number;
  qualityComponents: {
    profitability: number;
    consistency: number;
    efficiency: number;
    balanceSheet: number;
    valuation: number;
  };
  fcfYield: number;
  passedCriteria: string[];
  metricsSnapshot: any;
  runId: string;
  analysisTriggered: boolean;
  analysisId: string | null;
  verdict: string | null;
  mosPct: number | null;
  irrBase: number | null;
  verdictAt: any;
}

interface PreInflectionLogEntry {
  id: string;
  symbol: string;
  timestamp: any;
  source?: 'screener' | 'detector';
  preInflectionScore: number;
  priceAtDetection?: number | null;
  detectorResult?: boolean | null;
  signals: string[];
  metricsSnapshot: any;
  runId: string;
  analysisTriggered: boolean;
  analysisId: string | null;
  verdict: string | null;
  mosPct: number | null;
  irrBase: number | null;
  verdictAt: any;
}

interface MergedPreInflectionRow {
  screener: PreInflectionLogEntry;
  detector: PreInflectionLogEntry | null;
}

export const FunnelIntelligenceTab: React.FC = () => {
  const [activeSubTab, setActiveSubTab] = useState<'funnel' | 'preInflection' | 'runHistory'>('funnel');

  const [funnelEntries, setFunnelEntries] = useState<FunnelLogEntry[]>([]);
  const [preInflectionEntries, setPreInflectionEntries] = useState<PreInflectionLogEntry[]>([]);
  const [screenerRunEntries, setScreenerRunEntries] = useState<ScreenerRunEntry[]>([]);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  }>({ key: 'timestamp', direction: 'desc' });

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("Not authenticated");

      const headers = { 'Authorization': `Bearer ${token}` };

      const [funnelRes, preInflectionRes, runHistoryRes] = await Promise.all([
        fetch('/api/funnel/intelligence', { headers }),
        fetch('/api/funnel/pre-inflection-intelligence', { headers }),
        fetch('/api/funnel/run-history', { headers }),
      ]);

      if (!funnelRes.ok) throw new Error(`Funnel API error: ${funnelRes.statusText}`);
      if (!preInflectionRes.ok) throw new Error(`Pre-inflection API error: ${preInflectionRes.statusText}`);
      if (!runHistoryRes.ok) throw new Error(`Run history API error: ${runHistoryRes.statusText}`);

      const funnelData = await funnelRes.json();
      const preInflectionData = await preInflectionRes.json();
      const runHistoryData = await runHistoryRes.json();

      setFunnelEntries((funnelData.entries || []).map((e: any) => ({
        ...e,
        // Convert Firestore timestamps to string if needed, or simply handle them in render
        timestamp: typeof e.timestamp === 'string' ? new Date(e.timestamp) : (e.timestamp?.toDate ? e.timestamp.toDate() : new Date(e.timestamp?._seconds ? e.timestamp._seconds * 1000 : Date.now())),
        verdictAt: e.verdictAt ? (typeof e.verdictAt === 'string' ? new Date(e.verdictAt) : (e.verdictAt?.toDate ? e.verdictAt.toDate() : new Date(e.verdictAt?._seconds ? e.verdictAt._seconds * 1000 : Date.now()))) : null,
      })));
      setPreInflectionEntries((preInflectionData.entries || []).map((e: any) => ({
        ...e,
        timestamp: typeof e.timestamp === 'string' ? new Date(e.timestamp) : (e.timestamp?.toDate ? e.timestamp.toDate() : new Date(e.timestamp?._seconds ? e.timestamp._seconds * 1000 : Date.now())),
        verdictAt: e.verdictAt ? (typeof e.verdictAt === 'string' ? new Date(e.verdictAt) : (e.verdictAt?.toDate ? e.verdictAt.toDate() : new Date(e.verdictAt?._seconds ? e.verdictAt._seconds * 1000 : Date.now()))) : null,
      })));

      setScreenerRunEntries((runHistoryData.entries || []).map((e: any) => ({
        ...e,
        timestamp: typeof e.timestamp === 'string' ? new Date(e.timestamp) : (e.timestamp?.toDate ? e.timestamp.toDate() : new Date(e.timestamp?._seconds ? e.timestamp._seconds * 1000 : Date.now())),
      })));

    } catch (err: any) {
      console.error('Funnel intelligence fetch error:', err);
      setError(err.message || 'Failed to load intelligence data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortData = (data: any[]) => {
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      if (sortConfig.key === 'criteriaPassedCount') {
          aVal = a.passedCriteria?.length || 0;
          bVal = b.passedCriteria?.length || 0;
      }
      if (sortConfig.key === 'signalCount') {
          aVal = a.signals?.length || 0;
          bVal = b.signals?.length || 0;
      }

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // ── Pre-Inflection merge: screener entries as primary rows,
  // detector entries joined by symbol (most recent match per symbol).
  const screenerEntries = preInflectionEntries.filter(e => e.source !== 'detector');
  const detectorEntries = preInflectionEntries.filter(e => e.source === 'detector');

  const detectorBySymbol = new Map<string, PreInflectionLogEntry>();
  for (const d of [...detectorEntries].sort((a, b) =>
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  )) {
    detectorBySymbol.set(d.symbol, d);
  }

  const mergedRows: MergedPreInflectionRow[] = screenerEntries.map(s => ({
    screener: s,
    detector: detectorBySymbol.get(s.symbol) ?? null,
  }));

  const sortedMergedRows = [...mergedRows].sort((a, b) => {
    const aTime = new Date(a.screener.timestamp).getTime();
    const bTime = new Date(b.screener.timestamp).getTime();
    return sortConfig.key === 'timestamp'
      ? sortConfig.direction === 'desc' ? bTime - aTime : aTime - bTime
      : bTime - aTime; // default: newest first
  });

  const getActiveData = () => {
    return activeSubTab === 'funnel' ? funnelEntries : screenerEntries;
  };

  const sortedData = sortData(getActiveData());

  const summary = {
    total: getActiveData().length,
    analyzed: getActiveData().filter(e => e.analysisTriggered).length,
    fatPitch: getActiveData().filter(e => e.verdict?.toUpperCase() === 'FAT PITCH' || e.verdict?.toUpperCase() === 'BUY' || e.verdict?.toUpperCase() === 'ACCUMULATE').length,
    coreHold: getActiveData().filter(e => e.verdict?.toUpperCase() === 'CORE HOLD').length,
    passAvoid: getActiveData().filter(e => e.verdict?.toUpperCase() === 'PASS' || e.verdict?.toUpperCase() === 'AVOID').length,
    unanalyzed: getActiveData().filter(e => !e.verdict).length,
    // Pre-inflection only
    detectorRun: mergedRows.filter(r => r.detector !== null).length,
    inflectionConfirmed: mergedRows.filter(r => r.detector?.detectorResult === true).length,
  };
  const analysisRate = summary.total > 0 ? ((summary.analyzed / summary.total) * 100).toFixed(1) : '0.0';
  const inflectionRate = mergedRows.length > 0
    ? ((summary.inflectionConfirmed / mergedRows.length) * 100).toFixed(1)
    : '0.0';

  const runHistoryAvg = screenerRunEntries.length > 0 ? {
    candidates: Math.round(screenerRunEntries.reduce((s, e) => s + (e.stage1?.finalCandidates ?? 0), 0) / screenerRunEntries.length),
    survivors: (screenerRunEntries.reduce((s, e) => s + (e.stage2?.survivors ?? 0), 0) / screenerRunEntries.length).toFixed(1),
    results: (screenerRunEntries.reduce((s, e) => s + (e.stage3?.resultsCount ?? 0), 0) / screenerRunEntries.length).toFixed(1),
  } : null;

  const verdictColor = (verdict: string | null) => {
    if (!verdict) return 'text-ink-400 bg-surface border-ink-100'; // Actually uses stone-400 equivalent
    const v = verdict.toUpperCase();
    if (v === 'FAT PITCH' || v === 'BUY' || v === 'ACCUMULATE') return 'text-success-700 bg-success-50 border-success-100';
    if (v === 'CORE HOLD') return 'text-warning-700 bg-warning-50 border-warning-100';
    if (v === 'PASS' || v === 'AVOID') return 'text-danger-700 bg-danger-50 border-danger-100';
    return 'text-ink-600 bg-surface-2 border-ink-200';
  };

  const mosColorClass = (mos: number | null) => {
    if (mos === null) return 'text-ink-400';
    if (mos > 0) return 'text-success-700';
    if (mos < 0) return 'text-danger-700';
    return 'text-ink-600';
  };

  const formatDate = (date: any) => {
    if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const exportCSV = () => {
    if (activeSubTab === 'funnel') {
      const headers = ['symbol', 'date', 'runId', 'category', 'qualityScore', 'qualityComponent_profitability', 'qualityComponent_consistency', 'qualityComponent_efficiency', 'qualityComponent_balanceSheet', 'qualityComponent_valuation', 'fcfYield', 'criteriaPassedCount', 'passedCriteria', 'analysisTriggered', 'analysisId', 'verdict', 'mosPct', 'irrBase', 'verdictAt'];
      const rows = funnelEntries.map(e => [
        e.symbol,
        e.timestamp ? new Date(e.timestamp).toISOString() : '',
        e.runId || '',
        e.category || '',
        e.qualityScore !== null ? e.qualityScore.toFixed(1) : '',
        e.qualityComponents?.profitability || '',
        e.qualityComponents?.consistency || '',
        e.qualityComponents?.efficiency || '',
        e.qualityComponents?.balanceSheet || '',
        e.qualityComponents?.valuation || '',
        e.fcfYield !== null ? Number(e.fcfYield).toFixed(2) : '',
        e.passedCriteria?.length || 0,
        (e.passedCriteria || []).join('|'),
        e.analysisTriggered ? 'true' : 'false',
        e.analysisId || '',
        e.verdict || '',
        e.mosPct !== null ? e.mosPct.toFixed(2) : '',
        e.irrBase !== null ? e.irrBase.toFixed(2) : '',
        e.verdictAt ? new Date(e.verdictAt).toISOString() : ''
      ]);
      downloadCSV(headers, rows, 'funnel-intelligence.csv');
    } else if (activeSubTab === 'preInflection') {
      const headers = ['symbol', 'screenerDate', 'runId', 'screenerScore', 'screenStage', 'revenueAccelBps', 'marginTrend', 'capitalEfficiency', 'valuation', 'reversals', 'detectorRun', 'detectorSignal', 'suggestedAction', 'detectorScore', 'priceAtDetection', 'oracleVerdict', 'mosPct', 'irrBase', 'verdictAt'];
      const rows = mergedRows.map(({ screener: e, detector: d }) => [
        e.symbol,
        e.timestamp ? new Date(e.timestamp).toISOString() : '',
        e.runId || '',
        e.preInflectionScore !== null ? Number(e.preInflectionScore).toFixed(1) : '',
        e.metricsSnapshot?.screenStage ?? '',
        e.metricsSnapshot?.revenueAccelerationBps !== undefined ? Math.round(e.metricsSnapshot.revenueAccelerationBps) : '',
        e.metricsSnapshot?.marginTrend ?? '',
        e.metricsSnapshot?.capitalEfficiencyTrend ?? '',
        e.metricsSnapshot?.valuation ?? '',
        e.metricsSnapshot?.reversals !== undefined ? e.metricsSnapshot.reversals : '',
        d !== null ? 'true' : 'false',
        d === null ? '' : d.detectorResult ? 'Inflection' : 'No Signal',
        d?.metricsSnapshot?.suggestedAction ?? '',
        d?.preInflectionScore !== undefined ? Number(d.preInflectionScore).toFixed(1) : '',
        d?.priceAtDetection !== undefined && d?.priceAtDetection !== null ? Number(d.priceAtDetection).toFixed(2) : '',
        e.verdict || '',
        e.mosPct !== null ? e.mosPct.toFixed(2) : '',
        e.irrBase !== null ? e.irrBase.toFixed(2) : '',
        e.verdictAt ? new Date(e.verdictAt).toISOString() : ''
      ]);
      downloadCSV(headers, rows, 'pre-inflection-intelligence.csv');
    } else {
      const headers = ['runId', 'date', 'category', 'weekGroup', 'activeSectors', 'stage1_raw', 'stage1_final', 'stage2_survivors', 'stage2_qualified', 'stage2_watch', 'stage3_results', 'stage3_nearMisses', 'rej_data', 'rej_netIncome', 'rej_fcf', 'rej_fcfSwings', 'rej_interest', 'rej_roic', 'rej_oeRising'];
      const rows = screenerRunEntries.map(e => [
        e.runId || '',
        e.timestamp ? new Date(e.timestamp).toISOString() : '',
        e.category || '',
        e.weekGroup ?? '',
        (e.activeSectors || []).join('|'),
        e.stage1?.rawCandidates ?? '',
        e.stage1?.finalCandidates ?? '',
        e.stage2?.survivors ?? '',
        e.stage2?.qualifiedSurvivors ?? '',
        e.stage2?.watchSurvivors ?? '',
        e.stage3?.resultsCount ?? '',
        e.stage3?.nearMissesCount ?? '',
        e.stage2?.rejections?.insufficientData ?? '',
        e.stage2?.rejections?.netIncomeNeg ?? '',
        e.stage2?.rejections?.fcfNeg ?? '',
        e.stage2?.rejections?.fcfSwings ?? '',
        e.stage2?.rejections?.interestCoverage ?? '',
        e.stage2?.rejections?.roicFloor ?? '',
        e.stage2?.rejections?.oeNotRising ?? '',
      ]);
      downloadCSV(headers, rows, 'funnel-run-history.csv');
    }
  };

  const downloadCSV = (headers: string[], rows: any[][], filename: string) => {
    const csvContent = "data:text/csv;charset=utf-8," 
      + [headers.join(','), ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
     return (
       <div className="flex items-center justify-center min-h-[400px]">
         <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-oxblood"></div>
       </div>
     );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 p-1 bg-surface-2 rounded-md w-fit">
          <button
            onClick={() => setActiveSubTab('funnel')}
            className={`px-4 py-2 rounded-md text-[10px] font-medium uppercase tracking-widest transition-all ${
              activeSubTab === 'funnel' ? 'bg-oxblood text-paper shadow-sm' : 'text-ink-500 hover:text-ink'
            }`}
          >
            Funnel Picks
          </button>
          <button
            onClick={() => setActiveSubTab('preInflection')}
            className={`px-4 py-2 rounded-md text-[10px] font-medium uppercase tracking-widest transition-all ${
              activeSubTab === 'preInflection' ? 'bg-oxblood text-paper shadow-sm' : 'text-ink-500 hover:text-ink'
            }`}
          >
            Pre-Inflection Picks
          </button>
          <button
            onClick={() => setActiveSubTab('runHistory')}
            className={`px-4 py-2 rounded-md text-[10px] font-medium uppercase tracking-widest transition-all ${
              activeSubTab === 'runHistory' ? 'bg-oxblood text-paper shadow-sm' : 'text-ink-500 hover:text-ink'
            }`}
          >
            Run History
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-ink-200 rounded text-ink-600 hover:bg-surface-2 disabled:opacity-50 transition-colors"
          >
            Refresh
          </button>
          <button
            onClick={exportCSV}
            className="flex items-center gap-2 px-4 py-2 bg-surface border border-ink-200 rounded-md text-[10px] font-medium uppercase tracking-widest text-ink hover:bg-surface-2 transition-all"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
        </div>
      </div>

      {error ? (
        <div className="p-4 bg-danger-50 border border-danger-100 rounded-md flex items-start flex-col gap-3 text-danger-600">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span className="text-sm font-medium">{error}</span>
          </div>
          <button onClick={fetchData} className="px-4 py-2 border border-danger-200 rounded text-xs hover:bg-danger-100">Retry</button>
        </div>
      ) : (
        <>
          {activeSubTab === 'funnel' ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="oi-card p-4 flex flex-col justify-center">
                <span className="text-[10px] uppercase tracking-widest text-ink-500 mb-1">Total Picks</span>
                <span className="text-2xl font-mono text-ink">{summary.total}</span>
              </div>
              <div className="oi-card p-4 flex flex-col justify-center">
                <span className="text-[10px] uppercase tracking-widest text-ink-500 mb-1">Analyzed</span>
                <span className="text-2xl font-mono text-ink">{summary.analyzed}</span>
              </div>
              <div className="oi-card p-4 flex flex-col justify-center">
                <span className="text-[10px] uppercase tracking-widest text-ink-500 mb-1">Analysis Rate</span>
                <span className="text-2xl font-mono text-ink">{analysisRate}%</span>
              </div>
              <div className="oi-card p-4 flex flex-col justify-center">
                <span className="text-[10px] uppercase tracking-widest text-ink-500 mb-2">Verdict Distribution</span>
                <div className="flex flex-wrap gap-2">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${verdictColor('BUY')} border`}>{summary.fatPitch}</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${verdictColor('CORE HOLD')} border`}>{summary.coreHold}</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${verdictColor('PASS')} border`}>{summary.passAvoid}</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${verdictColor(null)} border`}>{summary.unanalyzed}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="oi-card p-4 flex flex-col justify-center">
                <span className="text-[10px] uppercase tracking-widest text-ink-500 mb-1">SCREENER PICKS</span>
                <span className="text-2xl font-mono text-ink">{mergedRows.length}</span>
              </div>
              <div className="oi-card p-4 flex flex-col justify-center">
                <span className="text-[10px] uppercase tracking-widest text-ink-500 mb-1">DETECTOR RUN</span>
                <span className="text-2xl font-mono text-ink">{summary.detectorRun} / {mergedRows.length}</span>
              </div>
              <div className="oi-card p-4 flex flex-col justify-center">
                <span className="text-[10px] uppercase tracking-widest text-ink-500 mb-1">INFLECTION RATE</span>
                <span className="text-2xl font-mono text-ink">{inflectionRate}%</span>
              </div>
              <div className="oi-card p-4 flex flex-col justify-center">
                <span className="text-[10px] uppercase tracking-widest text-ink-500 mb-2">VERDICT DISTRIBUTION</span>
                <div className="flex flex-wrap gap-2">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${verdictColor('BUY')} border`}>{summary.fatPitch}</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${verdictColor('CORE HOLD')} border`}>{summary.coreHold}</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${verdictColor('PASS')} border`}>{summary.passAvoid}</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${verdictColor(null)} border`}>{summary.unanalyzed}</span>
                </div>
              </div>
            </div>
          )}

          {activeSubTab !== 'runHistory' && (
          <div className="oi-card overflow-hidden overflow-x-auto">
            {activeSubTab === 'funnel' ? (
              <table className="w-full text-left border-collapse min-w-[800px]">
                <thead>
                  <tr className="bg-surface-2 border-b border-ink-200 text-[9px] font-medium uppercase tracking-widest text-ink-500">
                    <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('symbol')}>Symbol</th>
                    <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('timestamp')}>Date</th>
                    <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('category')}>Category</th>
                    <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('qualityScore')}>Quality Score</th>
                    <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('fcfYield')}>FCF Yield</th>
                    <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('criteriaPassedCount')}>Criteria Passed</th>
                    <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('analysisTriggered')}>Analyzed</th>
                    <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('verdict')}>Verdict</th>
                    <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('mosPct')}>MoS%</th>
                    <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('irrBase')}>Base IRR</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {sortedData.map((e, idx) => (
                    <tr key={idx} className="border-b border-ink-200 last:border-0 hover:bg-surface-2 transition-colors">
                      <td className="px-4 py-3 font-mono font-medium text-ink">{e.symbol}</td>
                      <td className="px-4 py-3 text-ink-600 text-xs">{formatDate(e.timestamp)}</td>
                      <td className="px-4 py-3 text-ink-600 truncate max-w-[120px]">{e.category}</td>
                      <td className="px-4 py-3 font-mono text-ink-600">{e.qualityScore !== null ? Number(e.qualityScore).toFixed(1) : '—'}</td>
                      <td className="px-4 py-3 font-mono text-ink-600">{e.fcfYield !== null ? `${Number(e.fcfYield).toFixed(1)}%` : '—'}</td>
                      <td className="px-4 py-3 font-mono text-ink-600">{e.passedCriteria?.length || 0}</td>
                      <td className="px-4 py-3 text-ink-600">{e.analysisTriggered ? <Check className="w-4 h-4 text-success-600" /> : <Minus className="w-4 h-4 text-ink-300" />}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-md text-[9px] font-medium uppercase tracking-widest border whitespace-nowrap ${verdictColor(e.verdict)}`}>
                          {e.verdict || '—'}
                        </span>
                      </td>
                      <td className={`px-4 py-3 font-mono ${mosColorClass(e.mosPct)}`}>{e.mosPct !== null ? `${e.mosPct > 0 ? '+' : ''}${e.mosPct.toFixed(1)}%` : '—'}</td>
                      <td className="px-4 py-3 font-mono text-ink-600">{e.irrBase !== null ? `${e.irrBase.toFixed(1)}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-left border-collapse min-w-[800px]">
                <thead>
                  <tr className="bg-surface-2 border-b border-ink-200 text-[9px] font-medium uppercase tracking-widest text-ink-500">
                    <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('symbol')}>Symbol</th>
                    <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('timestamp')}>Screened</th>
                    <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('preInflectionScore')}>Screener Score</th>
                    <th className="px-4 py-3">Detector Signal</th>
                    <th className="px-4 py-3">Suggested Action</th>
                    <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('verdict')}>Oracle Verdict</th>
                    <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('mosPct')}>MoS%</th>
                    <th className="px-4 py-3 cursor-pointer select-none" onClick={() => handleSort('irrBase')}>Base IRR</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {sortedMergedRows.map((row, idx) => {
                    const { screener: e, detector: d } = row;
                    const detectorSignal = d === null
                      ? null
                      : d.detectorResult === true
                        ? 'confirmed'
                        : 'rejected';
                    return (
                      <tr key={idx} className="border-b border-ink-200 last:border-0 hover:bg-surface-2 transition-colors">
                        <td className="px-4 py-3 font-mono font-medium text-ink">{e.symbol}</td>
                        <td className="px-4 py-3 text-ink-600 text-xs">{formatDate(e.timestamp)}</td>
                        <td className="px-4 py-3 font-mono text-ink-600">{e.preInflectionScore !== null ? Number(e.preInflectionScore).toFixed(1) : '—'}</td>
                        <td className="px-4 py-3">
                          {detectorSignal === 'confirmed' && (
                            <span className="px-2 py-0.5 rounded-md text-[9px] font-medium uppercase tracking-widest border text-success-700 bg-success-50 border-success-100 whitespace-nowrap">
                              ✓ Inflection
                            </span>
                          )}
                          {detectorSignal === 'rejected' && (
                            <span className="px-2 py-0.5 rounded-md text-[9px] font-medium uppercase tracking-widest border text-danger-700 bg-danger-50 border-danger-100 whitespace-nowrap">
                              ✗ No Signal
                            </span>
                          )}
                          {detectorSignal === null && (
                            <span className="text-ink-300 font-mono">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-ink-600 text-xs max-w-[160px] truncate">
                          {d?.metricsSnapshot?.suggestedAction ?? '—'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 rounded-md text-[9px] font-medium uppercase tracking-widest border whitespace-nowrap ${verdictColor(e.verdict)}`}>
                            {e.verdict || '—'}
                          </span>
                        </td>
                        <td className={`px-4 py-3 font-mono ${mosColorClass(e.mosPct)}`}>{e.mosPct !== null ? `${e.mosPct > 0 ? '+' : ''}${e.mosPct.toFixed(1)}%` : '—'}</td>
                        <td className="px-4 py-3 font-mono text-ink-600">{e.irrBase !== null ? `${e.irrBase.toFixed(1)}%` : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            
            {sortedData.length === 0 && (
              <div className="text-center py-12 text-ink-500 text-sm">
                No entries found
              </div>
            )}
          </div>
          )} {/* end activeSubTab !== 'runHistory' */}

          {/* ── Run History panel ─────────────────────────────────── */}
          {activeSubTab === 'runHistory' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="oi-card p-4 flex flex-col justify-center">
                  <span className="text-[10px] uppercase tracking-widest text-ink-500 mb-1">Total Runs</span>
                  <span className="text-2xl font-mono text-ink">{screenerRunEntries.length}</span>
                </div>
                <div className="oi-card p-4 flex flex-col justify-center">
                  <span className="text-[10px] uppercase tracking-widest text-ink-500 mb-1">Avg Candidates</span>
                  <span className="text-2xl font-mono text-ink">{runHistoryAvg?.candidates ?? '—'}</span>
                </div>
                <div className="oi-card p-4 flex flex-col justify-center">
                  <span className="text-[10px] uppercase tracking-widest text-ink-500 mb-1">Avg Survivors</span>
                  <span className="text-2xl font-mono text-ink">{runHistoryAvg?.survivors ?? '—'}</span>
                </div>
                <div className="oi-card p-4 flex flex-col justify-center">
                  <span className="text-[10px] uppercase tracking-widest text-ink-500 mb-1">Avg Results / Run</span>
                  <span className="text-2xl font-mono text-ink">{runHistoryAvg?.results ?? '—'}</span>
                </div>
              </div>

              <div className="oi-card overflow-hidden overflow-x-auto">
                {screenerRunEntries.length > 0 ? (
                  <table className="w-full text-left border-collapse min-w-[900px]">
                    <thead>
                      <tr className="bg-surface-2 border-b border-ink-200 text-[9px] font-medium uppercase tracking-widest text-ink-500">
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3">Cap</th>
                        <th className="px-4 py-3">Wk</th>
                        <th className="px-4 py-3">Sectors</th>
                        <th className="px-4 py-3 text-right">Cand</th>
                        <th className="px-4 py-3 text-right">Surv</th>
                        <th className="px-4 py-3 text-right">Qual / Watch</th>
                        <th className="px-4 py-3 text-right">Results / NM</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      {screenerRunEntries.map((entry, idx) => {
                        const isExpanded = expandedRunId === entry.runId;
                        const r = entry.stage2?.rejections;
                        return (
                          <React.Fragment key={idx}>
                            <tr
                              className="border-b border-ink-200 last:border-0 hover:bg-surface-2 transition-colors cursor-pointer"
                              onClick={() => setExpandedRunId(isExpanded ? null : entry.runId)}
                            >
                              <td className="px-4 py-3 text-ink-600 text-xs">{formatDate(entry.timestamp)}</td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-0.5 rounded text-[9px] font-medium uppercase tracking-widest border ${
                                  entry.category === 'large' ? 'text-ink-700 bg-surface-2 border-ink-200' :
                                  entry.category === 'mid' ? 'text-warning-700 bg-warning-50 border-warning-100' :
                                  'text-success-700 bg-success-50 border-success-100'
                                }`}>{entry.category}</span>
                              </td>
                              <td className="px-4 py-3 font-mono text-ink-600">{entry.weekGroup}</td>
                              <td className="px-4 py-3 text-ink-600 text-xs max-w-[200px] truncate">{(entry.activeSectors || []).join(' · ')}</td>
                              <td className="px-4 py-3 font-mono text-ink-600 text-right">{entry.stage1?.finalCandidates ?? '—'}</td>
                              <td className="px-4 py-3 font-mono text-ink-600 text-right">{entry.stage2?.survivors ?? '—'}</td>
                              <td className="px-4 py-3 font-mono text-ink-600 text-right">
                                <span className="text-success-700">{entry.stage2?.qualifiedSurvivors ?? '—'}</span>
                                <span className="text-ink-300 mx-1">/</span>
                                <span className="text-ink-500">{entry.stage2?.watchSurvivors ?? '—'}</span>
                              </td>
                              <td className="px-4 py-3 font-mono text-right">
                                <span className="text-success-700 font-medium">{entry.stage3?.resultsCount ?? '—'}</span>
                                <span className="text-ink-300 mx-1">/</span>
                                <span className="text-ink-500">{entry.stage3?.nearMissesCount ?? '—'}</span>
                              </td>
                              <td className="px-4 py-3 text-ink-400 text-xs">{isExpanded ? '▲' : '▼'}</td>
                            </tr>
                            {isExpanded && r && (
                              <tr className="bg-surface-2 border-b border-ink-100">
                                <td colSpan={9} className="px-4 py-3">
                                  <div className="flex flex-wrap gap-x-6 gap-y-2 text-[10px] font-medium uppercase tracking-widest">
                                    <span className="text-ink-500">Gate Rejections:</span>
                                    <span className="text-ink-600">Data <span className="font-mono text-ink">{r.insufficientData ?? 0}</span></span>
                                    <span className="text-ink-600">Net Income <span className="font-mono text-ink">{r.netIncomeNeg ?? 0}</span></span>
                                    <span className="text-ink-600">FCF <span className="font-mono text-ink">{r.fcfNeg ?? 0}</span></span>
                                    <span className="text-ink-600">FCF Swings <span className="font-mono text-ink">{r.fcfSwings ?? 0}</span></span>
                                    <span className="text-ink-600">Interest <span className="font-mono text-ink">{r.interestCoverage ?? 0}</span></span>
                                    <span className="text-ink-600">ROIC <span className="font-mono text-ink">{r.roicFloor ?? 0}</span></span>
                                    <span className="text-ink-600">OE Rising <span className="font-mono text-ink">{r.oeNotRising ?? 0}</span></span>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-12 text-ink-500 text-sm">
                    No runs logged yet. Run the Funnel to populate this view.
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};
